import { Trans } from '@lingui/macro';
import { useState } from "react";
import { RoomSimplified } from "../../models/Room";
import { Soribot } from "../../models/Soribot";
import { Fire } from "../../services/FireClient";
import { useAppUIContext } from "../../context/AppUIContext";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Loader2 } from "lucide-react";

export const SoribotRoomsSettingModal: React.FC<{ bot: Soribot }> = ({ bot }) => {
  const [inProgress, setInProgress] = useState(false);
  const [enabledRoomIds, setEnabledRoomIds] = useState<string[]>(bot?.roomIds ?? []);
  const [show, setShow] = useState(false);
  const [wsRooms, setWSRooms] = useState<RoomSimplified[]>([]);
  const { workspaces } = useAppUIContext();

  function onOpenChange(open: boolean) {
    setShow(open);
    if (open && bot?.workspaceId) {
      setEnabledRoomIds([...bot.roomIds]);
      fetchWorkspaceRooms(bot.workspaceId);
    }
  }

  async function updateBot() {
    bot.rooms = wsRooms.filter(r => enabledRoomIds.includes(r.id));
    setInProgress(true);
    await Fire.bot.updateSoribot(bot);
    setInProgress(false);
    setShow(false);
  }

  const workspaceName = workspaces.find(ws => ws.id === bot.workspaceId)?.name ?? bot.workspaceId;

  return (
    <Dialog open={show} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline"><Trans>Add/Remove Channels</Trans></Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px] h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle><Trans>{bot?.name} - Add/Remove Channels</Trans></DialogTitle>
          <DialogDescription><Trans>Select channels to connect to this bot.</Trans></DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4 flex-1 overflow-hidden flex flex-col">
          <div className="grid gap-2">
            <Label><Trans>Workspace</Trans></Label>
            <div className="text-sm font-medium p-2 bg-muted rounded">{workspaceName}</div>
          </div>

          {bot?.workspaceId && (
            <div className="flex flex-col flex-1 min-h-0">
              <Label className="mb-2"><Trans>Add or remove channels</Trans></Label>
              <ScrollArea className="flex-1 border rounded p-2">
                {inProgress ? (
                  <div className="flex justify-center p-4"><Loader2 className="animate-spin text-muted-foreground" /></div>
                ) : (
                  <div className="space-y-2">
                    {wsRooms.length > 0 ? wsRooms.map(r => (
                      <div key={r.id} className="flex items-center space-x-2 py-1">
                        <Checkbox
                          id={`room-${r.id}`}
                          checked={enabledRoomIds.some(roomId => r.id === roomId)}
                          onCheckedChange={(checked) => handleChange(checked, r.id)}
                        />
                        <Label htmlFor={`room-${r.id}`} className="font-normal cursor-pointer select-none">
                          {r.name}
                        </Label>
                      </div>
                    )) : <div className="text-sm text-muted-foreground"><Trans>No channels found</Trans></div>}
                  </div>
                )}
              </ScrollArea>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setShow(false)}><Trans>Cancel</Trans></Button>
          <Button onClick={updateBot} disabled={inProgress}>
            {inProgress && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Trans>Save</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  function handleChange(checked: boolean | string, roomId: string) {
    if (checked) {
      setEnabledRoomIds([...enabledRoomIds, roomId]);
    } else {
      setEnabledRoomIds(enabledRoomIds.filter(r => r != roomId));
    }
  }

  function fetchWorkspaceRooms(workspaceId: string) {
    setInProgress(true);
    Fire.ws.getWorkspaceRooms({ workspaceId }).then(r => {
      setWSRooms(r.rooms.sort((a, b) => a.name.localeCompare(b.name)));
      setInProgress(false);
    });
  }
}

export default SoribotRoomsSettingModal;